
import { Vue, Component, Prop, Emit } from 'vue-property-decorator'
import { ModalBox as Modal } from '@mg/micro/vue2/ModalBox'
import { HeaderUserLoginGoogle } from '@mg/micro/vue2/HeaderUserLoginGoogle'
import type { GoogleLoginData } from '@mg/micro/vue2/HeaderUserLoginGoogle'
import { HeaderUserLoginApple } from '@mg/micro/vue2/HeaderUserLoginApple'
import type { AppleLoginData } from '@mg/micro/vue2/HeaderUserLoginApple'
import {
  FormInput,
  HeaderUserWidgetSpacer,
  RoundedButton,
} from '@mg/micro/vue2'
import BasicButton from '@mg/web-components/Atoms/Buttons/BasicButton'
import { AxiosError } from 'axios'
import { LoginErrorResponse, SocialLoginResponse } from '~/utils/webApi.types'
import {
  GQLLocalAccount,
  GQLLocalOpenHeaderEnum,
  GQLLocalUiInput,
} from '~/graphql/__generated__/interfacesLocal'
import { GQL_LOCAL_MUTATION_SET_UI } from '~/graphql/gqlLocal/localUi'
import { GET_BASIC_USER_INFO } from '~/graphql/gql/user'
import { GQLUser } from '~/graphql/__generated__/interfaces'
import { GQL_LOCAL_GET_CART } from '~/graphql/gqlLocal/localCart'
import { GQL_LOCAL_ACCOUNT } from '~/graphql/gqlLocal/localAccount'
import HeaderUserLoginFb from '~/components/header/HeaderUserLoginFb.vue'
import { Shops } from '~/config/shops'

const GOOGLE_BUTTON_LOCALE: Record<string, string> = {
  [Shops.SI10SI]: 'sl',
  [Shops.HR10MA]: 'hr',
}

const APPLE_SDK_LOCALE: Record<string, string> = {
  [Shops.HR10MA]: 'hr_HR',
}

@Component<LoginForm>({
  apollo: {
    account: GQL_LOCAL_ACCOUNT,
    cart: {
      prefetch: false,
      query: GQL_LOCAL_GET_CART,
    },
    user: {
      query: GET_BASIC_USER_INFO,
      skip() {
        return !this.$shopConfig.user.id && !this.account?.isLogged
      },
    },
  },
  components: {
    RoundedButton,
    BasicButton,
    FormInput,
    HeaderUserLoginFb,
    HeaderUserLoginGoogle,
    HeaderUserLoginApple,
    HeaderUserWidgetSpacer,
    Modal,
  },
})
export default class LoginForm extends Vue {
  @Prop({ default: 'full', type: String }) submitButtonType: 'full' | 'normal'
  @Prop({ default: false, type: Boolean }) isOpen: boolean
  user: GQLUser
  account: GQLLocalAccount
  errorMessageShake = false
  isLoginInProgress = false
  isSocialSignInProgress = false
  errorMessage: string | null | undefined = null

  username: string = ''
  password: string = ''

  get isFullSubmit() {
    return this.submitButtonType === 'full'
  }

  get isSignInProgress(): boolean {
    return this.isSocialSignInProgress || this.isLoginInProgress
  }

  get webConfig() {
    return this.$shopConfig.webConfig
  }

  get googleButtonLocale(): string | undefined {
    return GOOGLE_BUTTON_LOCALE[this.$shopConfig.shopId]
  }

  get appleButtonLocale(): string | undefined {
    return APPLE_SDK_LOCALE[this.$shopConfig.shopId]
  }

  get googleOneTapEnabled(): boolean {
    return (
      this.webConfig.googleOneTapEnabled === '1' && !this.$shopConfig.user?.id
    )
  }

  async onGoogleLoggedIn({ credential }: GoogleLoginData) {
    this.isSocialSignInProgress = true
    try {
      const data = await this.$webApi.loginGoogle({ credential })
      await this.onUserLoggedIn(data)
    } catch (err) {
      this.isSocialSignInProgress = false
      this.$logger.error('[GoogleLogin] login failed', err as Error)
    }
  }

  async onAppleLoggedIn(data: AppleLoginData) {
    this.isSocialSignInProgress = true
    try {
      const response = await this.$webApi.loginApple({
        identityToken: data.identityToken,
        firstName: data.firstName || 'name',
        lastName: data.lastName || 'name',
        email: data.email,
      })
      await this.onUserLoggedIn(response)
    } catch (err) {
      this.isSocialSignInProgress = false
      this.$logger.warn('[AppleLogin] login failed', err)
    }
  }

  onSocialLoginError(prefix: string, err: unknown) {
    this.$logger.warn(`${prefix} failed`, err)
  }

  get googleLoginEnabled(): boolean {
    return !!this.$shopConfig.webConfig.googleClientId
  }

  get appleLoginEnabled(): boolean {
    return !!this.$shopConfig.webConfig.appleClientId
  }

  get socialLoginEnabled(): boolean {
    return this.googleLoginEnabled || this.appleLoginEnabled
  }

  login(event: Event): Promise<void> {
    const username = (document.getElementById('username') as HTMLInputElement)
      .value
    const password = (document.getElementById('password') as HTMLInputElement)
      .value

    event.preventDefault()
    const onError = (err: AxiosError<LoginErrorResponse>) => {
      this.errorMessage = err.response?.data.title
      this.errorMessageShake = true
      setTimeout(() => (this.errorMessageShake = false), 1000)
    }

    const userLoginBody = { isCheckout: false, password, username }

    return this.$webApi
      .login(userLoginBody)
      .then(this.onUserLoggedIn)
      .then(this.emitOnUserLoggedIn)
      .catch(onError)
      .then()
  }

  async onUserLoggedIn(data: SocialLoginResponse) {
    if (data?.ssoLoginUrl) {
      window.location.href = data.ssoLoginUrl
      return
    }
    if (data?.wso2LoginUrl) {
      window.location.href = data.wso2LoginUrl
      return
    }

    this.isLoginInProgress = true
    this.setUi({ isLoginInProgress: true })

    let willReloadPage = false
    try {
      await this.$apollo.queries.cart.refetch()
      await this.$apollo.queries.account.refetch()
      await this.$apollo.queries.user.refetch()

      willReloadPage = this.account.customer?.vipType !== null
      if (willReloadPage) {
        window.location.reload()
      }
      this.changeTab(null)
    } finally {
      if (!willReloadPage) {
        this.setUi({ isLoginInProgress: false, openHeaderTab: null })
        this.isLoginInProgress = false
        this.isSocialSignInProgress = false
      }
    }
  }

  @Emit()
  emitOnUserLoggedIn() {
    return true
  }

  setUi(ui: GQLLocalUiInput) {
    this.$apollo.mutate({
      mutation: GQL_LOCAL_MUTATION_SET_UI,
      variables: {
        ui,
      },
    })
  }

  changeTab(type: GQLLocalOpenHeaderEnum | null) {
    this.setUi({
      openHeaderTab: type,
    })
  }

  get fbLoginEnabled(): boolean {
    return false // this.$shopConfig.shopId !== Shops.HU20MA
  }
}
